/**
 * 全局状态管理（不依赖 Redux，使用 React Context）
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import request from '../utils/request';

const AuthContext = createContext(null);

// 系统设置默认值（仅文件大小限制，水印改为用户个人设置）
const DEFAULT_SYSTEM_SETTINGS = {
  import_max_size_mb: 10,
  export_max_size_mb: 50,
};

const initial_state = {
  token: localStorage.getItem('fdpush_token') || null,
  user: (() => {
    try {
      return JSON.parse(localStorage.getItem('fdpush_user')) || null;
    } catch {
      return null;
    }
  })(),
  loading: false,
  system_settings: DEFAULT_SYSTEM_SETTINGS,
};

function auth_reducer(state, action) {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        token: action.payload.token,
        user: action.payload.user,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        token: null,
        user: null,
        loading: false,
        system_settings: DEFAULT_SYSTEM_SETTINGS,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_SYSTEM_SETTINGS':
      return { ...state, system_settings: { ...DEFAULT_SYSTEM_SETTINGS, ...action.payload } };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(auth_reducer, initial_state);

  // 同步到 localStorage
  useEffect(() => {
    if (state.token) {
      localStorage.setItem('fdpush_token', state.token);
    } else {
      localStorage.removeItem('fdpush_token');
    }
    if (state.user) {
      localStorage.setItem('fdpush_user', JSON.stringify(state.user));
    } else {
      localStorage.removeItem('fdpush_user');
    }
  }, [state.token, state.user]);

  // 拉取系统公开配置（水印等）
  const fetch_system_settings = useCallback(async () => {
    try {
      const res = await request.get('/settings/public');
      if (res?.data) {
        dispatch({ type: 'SET_SYSTEM_SETTINGS', payload: res.data });
      }
    } catch {
      // 拉取失败不影响正常使用，使用默认值
    }
  }, []);

  // 登录后自动拉取系统设置
  useEffect(() => {
    if (state.token && state.user) {
      fetch_system_settings();
    }
  }, [state.token, state.user, fetch_system_settings]);

  const login = (token, user) => {
    dispatch({ type: 'LOGIN', payload: { token, user } });
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const update_user = (data) => {
    dispatch({ type: 'UPDATE_USER', payload: data });
  };

  // 检查卡密是否快过期（提前3天提醒）
  const is_card_expiring_soon = () => {
    if (!state.user || state.user.role === 'admin') return false;
    if (!state.user.card_expire_at) return false;
    const days_left = dayjs(state.user.card_expire_at).diff(dayjs(), 'day');
    return days_left >= 0 && days_left <= 3;
  };

  const is_card_valid = () => {
    if (!state.user) return false;
    if (state.user.role === 'admin') return true;
    if (!state.user.card_expire_at) return false;
    return dayjs().isBefore(dayjs(state.user.card_expire_at));
  };

  return (
    <AuthContext.Provider value={{
      ...state,
      is_authenticated: !!state.token && !!state.user,
      is_admin: state.user?.role === 'admin',
      is_card_valid: is_card_valid(),
      is_card_expiring_soon: is_card_expiring_soon(),
      login,
      logout,
      update_user,
      fetch_system_settings,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 必须在 AuthProvider 内部使用');
  return ctx;
}

